import { useEffect, useRef } from "react";

interface Props {
  onScrollTo: (id: string) => void;
}

const pillars = [
  {
    id: "info-security",
    icon: "ri-shield-keyhole-line",
    title: "信息安全解决方案",
    subtitle: "全方位立体防御",
    desc: "网络安全 · 数据安全 · 云上安全 · 安全服务",
    tags: ["等保合规", "主动防御", "零信任"],
    color: "from-[#0d1f3c] to-[#1a3a6b]",
    accent: "#4db8ff",
    bgImage: "https://readdy.ai/api/search-image?query=cybersecurity%20shield%20protection%20digital%20network%2C%20glowing%20blue%20security%20barrier%2C%20abstract%20technology%20defense%20visualization%2C%20dark%20navy%20background%2C%20professional%20enterprise%20security%2C%20no%20people&width=600&height=400&seq=hero_sec01&orientation=landscape",
  },
  {
    id: "it-infrastructure",
    icon: "ri-server-line",
    title: "基础架构解决方案",
    subtitle: "构建稳固数字基石",
    desc: "混合云 · 超融合 · 存储 · 容灾备份",
    tags: ["高可用", "弹性扩展", "全栈交付"],
    color: "from-[#0a1628] to-[#0d2d4a]",
    accent: "#38bdf8",
    bgImage: "https://readdy.ai/api/search-image?query=modern%20data%20center%20server%20racks%20with%20blue%20LED%20lighting%2C%20enterprise%20IT%20infrastructure%2C%20organized%20cable%20management%2C%20professional%20technology%20facility%2C%20dark%20background%2C%20no%20people&width=600&height=400&seq=hero_infra01&orientation=landscape",
  },
  {
    id: "intelligent-building",
    icon: "ri-building-4-line",
    title: "弱电智能化解决方案",
    subtitle: "赋能智慧空间",
    desc: "园区智能化 · 智能会议 · 数据中心",
    tags: ["一站式交付", "智慧互联", "场景化"],
    color: "from-[#0c1e35] to-[#0f3050]",
    accent: "#34d399",
    bgImage: "https://readdy.ai/api/search-image?query=smart%20building%20intelligent%20campus%20with%20modern%20architecture%2C%20building%20automation%20system%2C%20security%20cameras%20access%20control%2C%20professional%20smart%20office%20environment%2C%20dark%20blue%20night%20scene%2C%20no%20people&width=600&height=400&seq=hero_build01&orientation=landscape",
  },
];

export default function SolutionsHero({ onScrollTo }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const particles: { x: number; y: number; vx: number; vy: number; r: number; alpha: number }[] = [];
    for (let i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        r: Math.random() * 1.5 + 0.5,
        alpha: Math.random() * 0.5 + 0.1,
      });
    }

    let animId: number;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(77,184,255,${p.alpha})`;
        ctx.fill();
      });
      // draw lines
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(77,184,255,${0.08 * (1 - dist / 100)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(animId);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-[#060e1e]">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20dark%20technology%20background%20with%20digital%20network%20grid%2C%20deep%20space%20dark%20navy%20blue%2C%20glowing%20circuit%20board%20patterns%2C%20professional%20enterprise%20cybersecurity%20atmosphere%2C%20ultra%20wide%20panoramic%2C%20no%20people%2C%20no%20text&width=1920&height=1080&seq=solhero_bg01&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060e1e]/40 via-[#060e1e]/20 to-[#060e1e]"></div>
      </div>

      {/* Particle canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pt-32 pb-20 w-full">
        {/* Badge */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1a56db]/15 border border-[#1a56db]/30 rounded-full">
            <div className="w-1.5 h-1.5 rounded-full bg-[#4db8ff] animate-pulse"></div>
            <span className="text-[#4db8ff] text-xs font-medium tracking-widest uppercase">全栈解决方案</span>
          </div>
        </div>

        {/* Headline */}
        <div className="text-center mb-6">
          <h1 className="text-4xl lg:text-6xl font-bold text-white leading-tight mb-5">
            构建数字化时代的
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#4db8ff] to-[#22d3ee]">
              全栈式IT基础设施与安全防线
            </span>
          </h1>
          <p className="text-gray-400 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
            信息安全 · 基础架构 · 弱电智能化，三大核心领域深度覆盖，
            <br className="hidden lg:block" />
            端到端综合解决方案，护航企业数字化转型全程
          </p>
        </div>

        {/* Three Pillars */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-14">
          {pillars.map((p, idx) => (
            <button
              key={p.id}
              onClick={() => onScrollTo(p.id)}
              className="group relative rounded-2xl overflow-hidden text-left cursor-pointer transition-all duration-500 hover:-translate-y-2"
              style={{ animationDelay: `${idx * 0.1}s` }}
            >
              {/* Card bg image */}
              <div className="absolute inset-0">
                <img
                  src={p.bgImage}
                  alt={p.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${p.color} opacity-85 group-hover:opacity-75 transition-opacity duration-500`}></div>
              </div>

              {/* Card content */}
              <div className="relative z-10 p-7 lg:p-8 h-full flex flex-col min-h-[280px]">
                {/* Icon */}
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-xl mb-5 transition-transform duration-300 group-hover:scale-110"
                  style={{ backgroundColor: `${p.accent}20`, border: `1px solid ${p.accent}40` }}
                >
                  <i className={`${p.icon} text-2xl`} style={{ color: p.accent }}></i>
                </div>

                {/* Text */}
                <div className="flex-1">
                  <p className="text-xs font-medium mb-2 tracking-wider" style={{ color: p.accent }}>
                    {p.subtitle}
                  </p>
                  <h3 className="text-xl font-bold text-white mb-3 leading-snug">{p.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed mb-5">{p.desc}</p>
                  <div className="flex flex-wrap gap-2">
                    {p.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-1 text-xs rounded-full"
                        style={{ backgroundColor: `${p.accent}15`, color: p.accent, border: `1px solid ${p.accent}30` }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex items-center gap-2 mt-6 pt-5 border-t border-white/10">
                  <span className="text-sm text-gray-400 group-hover:text-white transition-colors duration-300">
                    查看详情
                  </span>
                  <div className="w-5 h-5 flex items-center justify-center transition-transform duration-300 group-hover:translate-x-1">
                    <i className="ri-arrow-right-line text-gray-400 group-hover:text-white transition-colors duration-300 text-sm"></i>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Scroll hint */}
        <div className="flex justify-center mt-14">
          <div className="flex flex-col items-center gap-2 animate-bounce">
            <span className="text-gray-500 text-xs tracking-widest">向下探索</span>
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-down-line text-gray-500 text-sm"></i>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
